import { Component, OnInit, Input, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core';
import { isQueryPresent, ResultSet } from '@cubejs-client/core';
import { CubeClient, TChartType } from '@cubejs-client/ngx';
import { BehaviorSubject, combineLatest, of, merge } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { ChartDataset, ChartOptions } from 'chart.js';
import { getDisplayedColumns, flattenColumns } from './utils';
import { CubeJsService } from 'src/app/services/cubeJs.service';
import moment from 'moment';
import { GranularityCubeJs } from 'src/app/enums/cubejs/granularity';
import { TranslateService } from '@ngx-translate/core';
import { environment } from 'src/environments/environment';
import { GraphType } from 'src/app/enums/cubejs/graph-type';
import { ConvertValueModel } from 'src/app/models/cubejs/ConvertValueModel';
import { ConvertValueType } from 'src/app/enums/cubejs/convertValueType.enum';
import { GlobalUtil } from 'src/app/services/utils/global.util';
import { UiLoadingModel } from 'src/app/tinea-components/loading/models/UiLoadingModel';

@Component({
    selector: 'app-query-renderer',
    templateUrl: './query-renderer.component.html',
    styleUrls: ['./query-renderer.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class QueryRendererComponent implements OnInit {

  @Output() hasError = new EventEmitter<boolean>();

  //event triggered when chart.js emit a click event
  @Output() clicked = new EventEmitter<any>();

  @Input('cubeQuery')
  cubeQuery$: BehaviorSubject<any>;

  @Input('pivotConfig')
  pivotConfig$: any;

  @Input('chartType')
  chartType$: any;

  // custom : unit of label : tmp. must be done by backend
  @Input('unit')
  unit$: any;

  @Input('graphType')
  graphType$: GraphType;

  @Input('convertValue')
  convertValue$: ConvertValueModel[];

  @Input() clickable: boolean;

  //storage of resultSet.tablePivot(pivotConfig)
  pivotedResult: any[] = [];

  //contain the cursor:pointer css class if chart is hovered and clickable
  itemHovered: string = '';

  chartType: any = null;
  isQueryPresent = false;
  error: string | null = null;

  displayedColumns: string[] = [];
  tableData: any[] = [];
  columnTitles: string[] = [];
  chartData: ChartDataset[] = [];
  chartLabels: string[] = [];
  chartOptions: ChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    onClick: (event, elements) => {
      this.onClickItem(event, elements);
    },
    onHover: (event, elements) => {
      this.onHoverItem(event, elements);
    }
  };
  noFillChartOptions: ChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    elements: {
      line: {
        fill: false,
      },
    },
  };
  numericValues: number[] = [];
  loadingModel: UiLoadingModel;
  loading = false;
  noData = false;

  constructor(private cubejsClient: CubeClient,
              private cubeJsService: CubeJsService,
              private globalUtil: GlobalUtil,
              private translateService: TranslateService) {}

  ngOnInit() {
    let onQueryStart;
    let onQueryLoad;
    this.setDefaultLoading();

    try {
      const queryId = window.location.hash.replace(/#\\/, '').split('=')[1];
      const { forQuery } = window.parent.window['__cubejsPlayground'] || {};
      onQueryStart = forQuery(queryId).onQueryStart;
      onQueryLoad = forQuery(queryId).onQueryLoad;
    } catch (_) {}

    combineLatest<[boolean, ResultSet, any, TChartType, string, GraphType, ConvertValueModel[]]>([
      this.cubeQuery$.pipe(
        switchMap((cubeQuery) => {
          return of(isQueryPresent(cubeQuery || {}));
        })
      ),
      this.cubeQuery$.pipe(
        switchMap((cubeQuery) => {
          this.error = null;
          if (!isQueryPresent(cubeQuery || {})) {
            return of(null);
          }
          this.loading = true;
          this.loadingModel.isLoading = true;

          if (typeof onQueryStart === 'function') {
            onQueryStart();
          }

          return merge(
            of(null),
            this.cubejsClient.load(cubeQuery).pipe(
              tap(response => {
                this.hasError.next(false);
              }),
              catchError((error) => {
                this.error = error.toString();
                console.error(error);
                this.hasError.next(true);
                this.loadingModel.isLoading = false;
                return of(null);
              }),
              
            )
          );
        })
      ),
      this.pivotConfig$,
      this.chartType$,
      this.unit$,
      of(this.graphType$),
      of(this.convertValue$)
    ]).subscribe(
      ([isQueryPresent, resultSet, pivotConfig, chartType, units, graphType, convertValue]: [
        boolean,
        ResultSet,
        any,
        TChartType,
        string,
        GraphType,
        ConvertValueModel[]
      ]) => {
        // tmp @@@
        this.chartOptions.scales = {
          y: {
            ticks: {
              callback: function(item) {
                return item + units;
              },
            }
          }
        };

        this.chartType = chartType;
        this.isQueryPresent = isQueryPresent;

        if (resultSet != null) {
          this.loading = false;
          this.loadingModel.isLoading = false;
        }

        if (typeof onQueryLoad === 'function') {
          onQueryLoad({
            resultSet,
            error: this.error,
          });
        }

        if (resultSet) {
          this.storePivotedResults(resultSet, pivotConfig);
          if (this.chartType === 'table') {
            this.updateTableData(resultSet, pivotConfig, convertValue);
          } else if (this.chartType === 'number') {
            this.updateNumericData(resultSet);
          } else {
            this.updateChartData(resultSet, pivotConfig, graphType, convertValue);
          }
        }
      }
    );
  }

  setDefaultLoading(): void {
    const model = new UiLoadingModel();
    this.loadingModel = model;
  }

  updateChartData(resultSet, pivotConfig, graphType: GraphType, convertValue: ConvertValueModel[]) {
    this.chartData = resultSet.series(pivotConfig).map((item) => {
      const formattedChartData = item.series.map((serie) => {
        convertValue?.forEach(cv => {
          serie.value = this.formatValue(serie.value, cv);
        });
        return serie.value;
      });
      const chartDataSets: ChartDataset = {
        label: item.title,
        data: formattedChartData,
        stack: 'a',
      };
      if (this.cubeJsService.hasDefinedColors(graphType)) {
        chartDataSets.backgroundColor = this.cubeJsService.getColorLabelByGraphType(graphType, item.key);
      }
      if (this.cubeJsService.hasDefinedHoverColors(graphType)) {
        chartDataSets.hoverBackgroundColor = this.cubeJsService.getColoHoverLabelByMeasureKey(item.key);
      }
      return chartDataSets;
    });

    this.chartLabels = resultSet.chartPivot(pivotConfig).map((row) => {
      return this.getFormattedXLabel(resultSet, row.x);
    });
    this.noData = this.chartData.length === 0 ? true : false;
  }

  formatValue(data, cv: ConvertValueModel) {
    if (cv.type === ConvertValueType.SECONDS) {
      data = this.convertSecondsToFormat(data);
    } else if (cv.type === ConvertValueType.FLOAT) {
      data = this.convertFloatFormat(data, cv);
    }
    return data;
  }

  updateTableData(resultSet, pivotConfig, convertValue: ConvertValueModel[]) {
    const tableData = resultSet.tablePivot(pivotConfig);
    this.tableData = tableData.map(data => {
      convertValue?.forEach( cv => {
        data[cv.measure] = this.formatValue(data[cv.measure], cv);
      });
      return data;
    });
    this.noData = this.tableData.length === 0 ? true : false;

    this.displayedColumns = getDisplayedColumns(
      resultSet.tableColumns(pivotConfig)
    );
    this.columnTitles = flattenColumns(resultSet.tableColumns(pivotConfig));
  }

  updateNumericData(resultSet) {
    this.numericValues = resultSet
      .seriesNames()
      .map((s) => resultSet.totalRow()[s.key]);
  }

  getFormattedXLabel(resultSet: ResultSet<any>, x: string): string {
    const granularity: GranularityCubeJs = resultSet.query()?.timeDimensions[0]?.granularity as GranularityCubeJs;

    const xDate = moment(x);
      if (xDate.isValid()) {
        return this.getFormattedXLabelDate(granularity, xDate.utc(true));
      } else {
        return x;
      }
  }

  getFormattedXLabelDate(granularity: GranularityCubeJs, utcDate: moment.Moment): string {
    let label = '';
    switch (granularity) {
      case GranularityCubeJs.SECOND:
        label = utcDate.local().locale(this.translateService.currentLang()).format(environment.core.datetimeFormat.datetime);
        break;
      case GranularityCubeJs.MINUTE:
        label = utcDate.local().locale(this.translateService.currentLang()).format(environment.core.datetimeFormat.dateHourMinute);
        break;
      case GranularityCubeJs.HOUR:
        label = utcDate.local().format(environment.core.datetimeFormat.dateHour);
        break;
      case GranularityCubeJs.DAY:
        label = utcDate.locale(this.translateService.currentLang()).format(environment.core.datetimeFormat.dayDate);
        break;
      case GranularityCubeJs.WEEK:
        label = utcDate.format('WW');
        break;
      case GranularityCubeJs.MONTH:
        label = utcDate.locale(this.translateService.currentLang()).format(environment.core.datetimeFormat.shortMonth);
        break;
      case GranularityCubeJs.QUARTER:
        console.warn('pas traité !');
        break;
      case GranularityCubeJs.YEAR:
        label = utcDate.format(environment.core.datetimeFormat.year);
        break;
      default:
        console.error('getFormattedXLabelDate: unknown');
        break;
    }

    return label;
  }

  convertSecondsToFormat(secondsInput: number) {
    const seconds = Number(secondsInput);
    const d = Math.floor(seconds / (3600 * 24));
    const h = Math.floor(seconds % (3600 * 24) / 3600);
    const m = Math.floor(seconds % 3600 / 60);
    const s = Math.floor(seconds % 60);

    const sString = s < 10 ? '0' + s : s;
    const mString = m < 10 ? '0' + m : m;
    const hString = h < 10 ? '0' + h : h;

    const dDisplay = d > 0 ? (d + 'j ') : '';
    const hDisplay = h > 0 ? (hString + ':') : '00:';
    const mDisplay = m > 0 ? (mString + ':') : '00:';
    const sDisplay = s > 0 ? (sString + '') : '00';

    return dDisplay + hDisplay + mDisplay + sDisplay;

  }

  convertFloatFormat(floatInput: string, convertValue: ConvertValueModel) {
    return this.globalUtil.convertNumberWithDecimal(floatInput, convertValue.format as number);
  }

  storePivotedResults(resultSet: ResultSet, pivotConfig: any){
    this.pivotedResult = resultSet.tablePivot(pivotConfig);
  }

  //emit the data related to the graph item clicked
  onClickItem(event: any, array: any) {
    const i = array.length > 0 ? array[0].index : null;
    if (i !== null && this.storePivotedResults.length > 0) this.clicked.emit(this.pivotedResult[i]);
  }

  //set cursor-pointer if the chart is define as clickable and a chart item is hovered
  onHoverItem(event: any, array: any){
    if (this.clickable) this.itemHovered = array.length > 0 ? 'item-hovered' : '';
  }
}
